// Image helpers for the paste flow (issue #1504).
// In-memory only — nothing here writes to localStorage, IndexedDB or the network.

export type SupportedImageMime = 'image/png' | 'image/jpeg' | 'image/webp';

/** Pre-downscale rejection ceiling — anything bigger than this never enters the pipeline. */
export const MAX_INPUT_BYTES = 10 * 1024 * 1024;

/** Post-downscale ceiling. Stays well under the API's MaxLength on the base64 field. */
export const MAX_OUTPUT_BYTES = 5 * 1024 * 1024;

/** Largest edge after downscale. ~1600 px is plenty for screenshots while keeping tokens reasonable. */
export const DOWNSCALE_MAX_DIMENSION = 1600;

const SUPPORTED_MIMES: ReadonlySet<string> = new Set(['image/png', 'image/jpeg', 'image/webp']);

export class ImageTooLargeError extends Error {
    constructor() {
        super('Image too large');
        this.name = 'ImageTooLargeError';
    }
}

export class UnsupportedImageFormatError extends Error {
    constructor(mime: string) {
        super(`Unsupported image format: ${mime}`);
        this.name = 'UnsupportedImageFormatError';
    }
}

/**
 * Pull an image out of a paste/clipboard event. Returns null if the user pasted text or
 * any other non-image payload.
 */
export const clipboardEventToBlob = (e: ClipboardEvent | React.ClipboardEvent): Blob | null => {
    const items = (e as ClipboardEvent).clipboardData?.items;
    if (!items) return null;
    for (const item of Array.from(items)) {
        if (item.kind === 'file' && item.type.startsWith('image/')) {
            return item.getAsFile();
        }
    }
    return null;
};

/**
 * Read a Blob into a base64 string (without the data-URL prefix).
 */
const blobToBase64 = (blob: Blob): Promise<string> =>
    new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => {
            const result = reader.result;
            if (typeof result !== 'string') {
                reject(new Error('FileReader returned non-string'));
                return;
            }
            const commaIdx = result.indexOf(',');
            resolve(commaIdx >= 0 ? result.slice(commaIdx + 1) : result);
        };
        reader.onerror = () => {
            reject(reader.error ?? new Error('FileReader failed'));
        };
        reader.readAsDataURL(blob);
    });

/**
 * Downscale an image Blob with a canvas if either dimension exceeds the cap, then return
 * the encoded base64 + the chosen MIME type. JPEG is preferred for size; PNG kept when
 * the input was PNG with transparency.
 */
export interface DownscaleOptions {
    /**
     * Always re-encode through the canvas, even when no resize is needed. Drops EXIF and other
     * metadata (GPS, device, time): required for images stored and shown to a human agent.
     */
    stripMetadata?: boolean;
}

export const downscaleImage = async (
    blob: Blob,
    maxDim: number = DOWNSCALE_MAX_DIMENSION,
    options?: DownscaleOptions,
): Promise<{ base64: string; mimeType: SupportedImageMime }> => {
    if (!SUPPORTED_MIMES.has(blob.type)) {
        throw new UnsupportedImageFormatError(blob.type);
    }
    if (blob.size > MAX_INPUT_BYTES) {
        throw new ImageTooLargeError();
    }

    const objectUrl = URL.createObjectURL(blob);
    try {
        const img = await loadImage(objectUrl);
        const longest = Math.max(img.naturalWidth, img.naturalHeight);
        const scale = longest > maxDim ? maxDim / longest : 1;

        // No resize needed — just encode the original. Saves a round-trip through the canvas.
        if (scale === 1 && !options?.stripMetadata) {
            const base64 = await blobToBase64(blob);
            ensureWithinOutputCap(base64);
            return { base64, mimeType: blob.type as SupportedImageMime };
        }

        const targetW = Math.round(img.naturalWidth * scale);
        const targetH = Math.round(img.naturalHeight * scale);
        const canvas = document.createElement('canvas');
        canvas.width = targetW;
        canvas.height = targetH;
        const ctx = canvas.getContext('2d');
        if (!ctx) {
            throw new Error('Canvas 2d context unavailable');
        }
        // Keep PNG when source was PNG (preserves transparency); otherwise JPEG for size.
        const outputMime: SupportedImageMime = blob.type === 'image/png' ? 'image/png' : 'image/jpeg';
        // JPEG has no alpha: unfilled pixels of a transparent WebP would encode as black.
        if (outputMime === 'image/jpeg') {
            ctx.fillStyle = '#ffffff';
            ctx.fillRect(0, 0, targetW, targetH);
        }
        ctx.drawImage(img, 0, 0, targetW, targetH);
        const quality = outputMime === 'image/jpeg' ? 0.85 : undefined;
        const downscaled: Blob = await new Promise((resolve, reject) => {
            canvas.toBlob(
                (b) => {
                    if (b) {
                        resolve(b);
                    } else {
                        reject(new Error('canvas.toBlob returned null'));
                    }
                },
                outputMime,
                quality,
            );
        });

        const base64 = await blobToBase64(downscaled);
        ensureWithinOutputCap(base64);
        return { base64, mimeType: outputMime };
    } finally {
        URL.revokeObjectURL(objectUrl);
    }
};

const ensureWithinOutputCap = (base64: string) => {
    // base64 byte length ≈ raw bytes × 4/3
    const approxBytes = Math.ceil((base64.length * 3) / 4);
    if (approxBytes > MAX_OUTPUT_BYTES) {
        throw new ImageTooLargeError();
    }
};

const loadImage = (src: string): Promise<HTMLImageElement> =>
    new Promise((resolve, reject) => {
        const img = new Image();
        img.onload = () => {
            resolve(img);
        };
        img.onerror = () => {
            reject(new Error('Failed to load image for downscaling'));
        };
        img.src = src;
    });
